import React from 'react';
import { 
  DollarSign, 
  TrendingUp, 
  Package, 
  CheckCircle, 
  XCircle,
  Clock,
  AlertCircle,
  Wallet
} from 'lucide-react';

function DriverStatisticsCards({ statistics, type, deliveries = [] }) {
  const statusColors = {
    entregado: 'text-green-600 bg-green-50',
    cancelado: 'text-slate-600 bg-slate-50',
    rechazado: 'text-red-600 bg-red-50',
    pending: 'text-yellow-600 bg-yellow-50',
    pospuesto: 'text-orange-600 bg-orange-50',
    encamino: 'text-blue-600 bg-blue-50'
  };

  const statusLabels = {
    entregado: 'Entregado',
    cancelado: 'Cancelado',
    rechazado: 'Rechazado',
    pending: 'Pendiente',
    pospuesto: 'Pospuesto',
    encamino: 'En Camino'
  };

  const statusIcons = {
    entregado: CheckCircle,
    cancelado: XCircle,
    rechazado: AlertCircle,
    pending: Clock,
    pospuesto: Clock,
    encamino: TrendingUp
  };

  // Calculate FastMax service fee
  function calculateFastMaxFee() {
    // If backend already calculated it, use that value
    if (statistics.fastmax_service_fee !== undefined) {
      return statistics.fastmax_service_fee;
    }

    // Fallback calculation if not provided by backend
    const chargeableDeliveries = (statistics.status_counts?.entregado || 0) + 
                                  (statistics.status_counts?.rechazado || 0);
    
    if (chargeableDeliveries === 0) {
      return 0;
    }

    const hasProvider = type === 'provider' || statistics.has_provider;
    
    if (hasProvider && deliveries && deliveries.length > 0) {
      // Calculate based on individual delivery fees
      let totalFee = 0;
      
      deliveries.forEach(delivery => {
        const status = delivery.status?.toLowerCase();
        
        // Only count entregado and rechazado
        if (status === 'entregado' || status === 'rechazado') {
          if (delivery.quince_soles) {
            totalFee += 15;
          } else if (delivery.veinte_soles) {
            totalFee += 20;
          } else {
            totalFee += 7.9; // Default provider rate
          }
        }
      });
      
      return totalFee;
    } else if (hasProvider) {
      // Provider users without delivery details: 7.9 soles per delivery
      return chargeableDeliveries * 7.9;
    } else {
      // Regular users: 15 soles for 1 delivery, 9.9 for 2 or more
      if (chargeableDeliveries === 1) {
        return 15;
      } else {
        return chargeableDeliveries * 9.9;
      }
    }
  }

  const fastMaxServiceFee = calculateFastMaxFee();
  const totalCollected = (statistics.total_charged || 0) + (statistics.total_fastmax || 0);
  const totalExchange = statistics.total_exchange || 0;
  const totalToDeposit = totalCollected - fastMaxServiceFee - totalExchange;

  return (
    <div className="mb-6 space-y-6">
      {/* Financial Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-brand-primary to-brand-primary/80 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-8 h-8" />
            <span className="text-xs font-semibold opacity-90 font-montserrat">TOTAL COBRADO</span>
          </div>
          <p className="text-3xl font-bold font-montserrat">
            S/. {statistics.total_charged?.toFixed(2) || '0.00'}
          </p>
        </div>

        <div className="bg-gradient-to-br from-brand-gold to-brand-gold/80 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-8 h-8" />
            <span className="text-xs font-semibold opacity-90 font-montserrat">FASTMAX</span>
          </div>
          <p className="text-3xl font-bold font-montserrat">
            S/. {statistics.total_fastmax?.toFixed(2) || '0.00'}
          </p>
        </div>

        <div className="bg-gradient-to-br from-brand-green to-brand-green/80 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Package className="w-8 h-8" />
            <span className="text-xs font-semibold opacity-90 font-montserrat">PROVEEDOR</span>
          </div>
          <p className="text-3xl font-bold font-montserrat">
            S/. {statistics.total_provider?.toFixed(2) || '0.00'}
          </p>
        </div>

        <div className="bg-gradient-to-br from-slate-700 to-slate-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-8 h-8" />
            <span className="text-xs font-semibold opacity-90 font-montserrat">TOTAL</span>
          </div>
          <p className="text-3xl font-bold font-montserrat">
            S/. {totalCollected.toFixed(2)}
          </p>
        </div>

        <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Wallet className="w-8 h-8" />
            <span className="text-xs font-semibold opacity-90 font-montserrat">A DEPOSITAR</span>
          </div>
          <p className="text-3xl font-bold font-montserrat">
            S/. {totalToDeposit.toFixed(2)}
          </p>
          <div className="text-xs mt-2 space-y-1 opacity-80 font-montserrat">
            <p>Servicio: S/. {fastMaxServiceFee.toFixed(2)}</p>
            {totalExchange > 0 && (
              <p>Cambio: S/. {totalExchange.toFixed(2)}</p>
            )}
          </div>
        </div>
      </div>

      {/* Status Breakdown */}
      <div className="bg-white rounded-xl border-2 border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-800 font-montserrat mb-4">
          Estado de Entregas
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {Object.entries(statistics.status_counts || {}).map(([status, count]) => {
            const Icon = statusIcons[status] || Package;
            return (
              <div
                key={status}
                className={`${statusColors[status] || 'text-slate-600 bg-slate-50'} rounded-lg p-4 text-center`}
              >
                <Icon className="w-6 h-6 mx-auto mb-2" />
                <p className="text-2xl font-bold font-montserrat mb-1">{count}</p>
                <p className="text-xs font-semibold font-montserrat">
                  {statusLabels[status] || status}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border-2 border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-slate-600 font-montserrat font-semibold">TASA DE ÉXITO</p>
              <p className="text-2xl font-bold text-green-600 font-montserrat">
                {statistics.success_rate?.toFixed(1) || '0.0'}%
              </p>
            </div>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-green-600 rounded-full transition-all"
              style={{ width: `${statistics.success_rate || 0}%` }}
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border-2 border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-red-100 rounded-lg">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-xs text-slate-600 font-montserrat font-semibold">TASA DE RECHAZO</p>
              <p className="text-2xl font-bold text-red-600 font-montserrat">
                {statistics.rejection_rate?.toFixed(1) || '0.0'}%
              </p>
            </div>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-red-600 rounded-full transition-all"
              style={{ width: `${statistics.rejection_rate || 0}%` }}
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border-2 border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-slate-600 font-montserrat font-semibold">PRECISIÓN HORARIA</p>
              <p className="text-2xl font-bold text-blue-600 font-montserrat">
                {statistics.time_accuracy?.toFixed(1) || '0.0'}%
              </p>
            </div>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-600 rounded-full transition-all"
              style={{ width: `${statistics.time_accuracy || 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Total Deliveries */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold opacity-90 font-montserrat mb-1">TOTAL DE ENTREGAS</p>
            <p className="text-4xl font-bold font-montserrat">
              {statistics.total_deliveries || 0}
            </p>
          </div>
          <Package className="w-16 h-16 opacity-20" />
        </div>
      </div>
    </div>
  );
}

export default DriverStatisticsCards;